import InterfaceColorControl from './InterfaceColorControl.jsx'
import MarketWidgetSession from '../workspace/MarketWidgetSession.jsx'
import CommandHandlers from '../../commands/CommandHandlers.jsx'
import RetainedDashboardOutlet from './RetainedDashboardOutlet.jsx'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { Menu, X, Moon, Sun, RefreshCw, AlertTriangle, User, Settings, LogOut, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useAutoSyncContext } from '../../hooks/useAutoSync'
import { useSessionEnforcement } from '../../hooks/useSessionEnforcement'
import {
  getLastPositionWorkspacePage,
  usePageTracking,
} from '../../hooks/usePageTracking'
import { usePrivacy } from '../../hooks/usePrivacy'
import { AccountNicknamesProvider } from '../../hooks/useAccountNicknames'
import { applyThemeBuilderColors } from '../../lib/themeBuilder'
import { hydrateThemeBuilderStorageFromServer } from '../../lib/themePreferenceStore'
import NavbarClock from '../NavbarClock'
import PaperModeBanner from '../PaperModeBanner'
import { NotificationButton, NotificationViewport } from '../NotificationCenter'
import { getRuntimeEnvironment } from '../../lib/runtimeEnvironment'
import { isPositionWorkspacePath } from '../../lib/positionWorkspaceRoute'
import { CommandProvider } from '../../commands/CommandProvider'
import MarketDataOutageAlert from '../marketData/MarketDataOutageAlert'

// ── Navigation config ──────────────────────────────────────────────────────

const TOP_NAV = [
  { to: '/dashboard',         label: 'Dashboard',    match: '/dashboard' },
  { to: '/accounts/summary',  label: 'Accounts',     match: '/accounts' },
  { to: '/positions/current', label: 'Positions',    match: '/positions' },
  { to: '/strategies',        label: 'Strategies',   match: '/strategies' },
  { to: '/analytics',         label: 'Analytics',    match: '/analytics' },
]

const SUB_NAV = {
  '/positions': [
    { to: '/positions/current',          label: 'Current Positions' },
    { to: '/positions/board',            label: 'Position Board' },
    { to: '/positions/planner',          label: 'Position Planner' },
    { to: '/positions/history',          label: 'Trade Log' },
    {
      to: '/positions/current',
      label: 'Position Workspace',
      getTo: getLastPositionWorkspacePage,
      isActive: isPositionWorkspacePath,
    },
  ],
}

// ── Tab class helpers ──────────────────────────────────────────────────────

function topTabCls(isActive) {
  return [
    'flex items-center px-4 h-12 text-sm font-semibold transition-colors whitespace-nowrap',
    isActive
      ? 'text-ink'
      : 'text-inkMuted hover:text-ink',
  ].join(' ')
}

function desktopMenuLinkCls(isActive) {
  return [
    'flex items-center px-4 py-2.5 text-xs font-semibold transition-colors whitespace-nowrap',
    isActive
      ? 'bg-canvas2 text-ink'
      : 'text-inkMuted hover:bg-canvas2 hover:text-ink',
  ].join(' ')
}

function resolvedNavTarget(item) {
  return item.getTo ? item.getTo() : item.to
}

// ── Navbar status sub-components ──────────────────────────────────────────
// These consume AutoSync context so Layout itself doesn't re-render each
// time sync state flips — updates are contained to the navbar.

const SCHWAB_RECONNECT_PATH = '/api/schwab/auth?action=login'

function SchwabAuthWarningLink({ children, ...props }) {
  return (
    <a href={SCHWAB_RECONNECT_PATH} title="Reconnect Schwab" {...props}>
      {children}
    </a>
  )
}

function DesktopNavbarStatus() {
  const { lastSynced, syncing, tokenWarning, triggerSync } = useAutoSyncContext()
  return (
    <>
      {tokenWarning && (
        <SchwabAuthWarningLink
          className={`hidden md:flex items-center gap-1.5 font-mono text-[10px] px-2 py-1 rounded-lg border transition-colors ${
            tokenWarning.level === 'expired'
              ? 'text-ember border-ember/40 bg-ember/10 hover:bg-ember/20'
              : 'text-[#c4953a] border-[#c4953a]/40 bg-[#c4953a]/10 hover:bg-[#c4953a]/20'
          }`}
        >
          <AlertTriangle size={10} />
          {tokenWarning.level === 'expired'
            ? 'Schwab auth expired — reconnect now'
            : `Schwab auth expires ${tokenWarning.daysRemaining === 1 ? 'tomorrow' : `in ${tokenWarning.daysRemaining}d`}`}
        </SchwabAuthWarningLink>
      )}

      {(syncing || lastSynced) && (
        <button
          onClick={triggerSync}
          disabled={syncing}
          className="hidden md:flex items-center gap-1.5 font-mono text-[10px] text-inkDim hover:text-inkMuted transition-colors disabled:cursor-default"
          title={syncing ? 'Syncing…' : 'Click to sync now'}
        >
          <RefreshCw size={10} className={syncing ? 'animate-spin' : ''} />
          {syncing
            ? 'Syncing…'
            : lastSynced
              ? `Synced ${lastSynced.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              : ''}
        </button>
      )}
      <NavbarClock />
    </>
  )
}

function MobileMenuStatus({ onNavigate }) {
  const { lastSynced, syncing, tokenWarning, triggerSync } = useAutoSyncContext()
  return (
    <>
      {(syncing || lastSynced) && (
        <button
          onClick={triggerSync}
          disabled={syncing}
          className="px-3 pt-0.5 flex items-center gap-1.5 font-mono text-[10px] text-inkDim hover:text-inkMuted transition-colors disabled:cursor-default w-full"
          title={syncing ? 'Syncing…' : 'Click to sync now'}
        >
          <RefreshCw size={9} className={syncing ? 'animate-spin' : ''} />
          {syncing
            ? 'Syncing…'
            : lastSynced
              ? `Synced ${lastSynced.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              : ''}
        </button>
      )}
      {tokenWarning && (
        <SchwabAuthWarningLink
          onClick={onNavigate}
          className={`mx-3 mt-1 flex items-center gap-1.5 font-mono text-[10px] px-2 py-1 rounded-lg border ${
            tokenWarning.level === 'expired'
              ? 'text-ember border-ember/40 bg-ember/10'
              : 'text-[#c4953a] border-[#c4953a]/40 bg-[#c4953a]/10'
          }`}
        >
          <AlertTriangle size={9} />
          {tokenWarning.level === 'expired'
            ? 'Schwab auth expired — reconnect'
            : `Auth expires ${tokenWarning.daysRemaining === 1 ? 'tomorrow' : `in ${tokenWarning.daysRemaining}d`}`}
        </SchwabAuthWarningLink>
      )}
    </>
  )
}

// ── Layout ─────────────────────────────────────────────────────────────────

export default function Layout() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(null)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('bwc-dark-mode')
    return saved !== null ? saved === 'true' : true
  })
  const { privacyMode, togglePrivacy } = usePrivacy()
  const runtimeEnvironment = getRuntimeEnvironment()
  useSessionEnforcement()
  usePageTracking()

  useEffect(() => {
    const applyTheme = () => {
      const saved = localStorage.getItem('bwc-dark-mode')
      const next = saved !== null ? saved === 'true' : true
      setDark(next)
      document.documentElement.classList.toggle('dark', next)
      applyThemeBuilderColors(next ? 'dark' : 'light')
    }
    applyTheme()
    window.addEventListener('storage', applyTheme)
    window.addEventListener('bwc-theme-change', applyTheme)
    return () => {
      window.removeEventListener('storage', applyTheme)
      window.removeEventListener('bwc-theme-change', applyTheme)
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    hydrateThemeBuilderStorageFromServer({ userId: user.id }).then(() => {
      if (!cancelled) applyThemeBuilderColors()
    })
    return () => { cancelled = true }
  }, [user?.id])

  // Close user menu on outside click or Escape.
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false)
      }
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        const focusedSection = document.activeElement?.closest?.('[data-desktop-nav-section]')
        const focusedSectionTrigger = focusedSection?.querySelector('a[aria-haspopup="true"]')
        if (focusedSectionTrigger && focusedSectionTrigger !== document.activeElement) {
          focusedSectionTrigger.focus()
        }
        setDesktopMenuOpen(null)
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const handleSignOut = async () => {
    setUserMenuOpen(false)
    await signOut()
    navigate('/auth')
  }

  const toggleDark = () => {
    const stored = localStorage.getItem('bwc-dark-mode')
    const current = stored !== null ? stored === 'true' : dark
    const next = !current
    localStorage.setItem('bwc-dark-mode', String(next))
    document.documentElement.classList.toggle('dark', next)
    window.dispatchEvent(new Event('bwc-theme-change'))
  }

  const isDashboard = location.pathname.replace(/\/$/, '') === '/dashboard'
  const isChartDemo = location.pathname === '/__demo/position-workspace-chart'
  const isPositionWorkspace = isPositionWorkspacePath(location.pathname)

  return (
    <CommandProvider userId={user?.id}>
      <CommandHandlers handlers={{
        'app.privacy.toggle': togglePrivacy,
        'app.theme.toggle': toggleDark,
        'app.account.open': () => { if (window.innerWidth < 768) setMobileOpen(true); else setUserMenuOpen(true) },
      }} />
      <AccountNicknamesProvider>
      <div data-testid="app-shell" className="h-screen flex flex-col bg-parchment overflow-hidden">

      {/* ── Top navigation ──────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-canvas border-b border-border w-full max-w-full">

        {/* Main bar — full width */}
        <div data-testid="navbar-main-bar" className="px-3 md:px-4">
          <div className="flex items-center h-12">

            {/* Brand */}
            <Link
              to="/dashboard"
              aria-label="Erevis home"
              data-testid="navbar-brand"
              className="group mr-2 flex h-12 shrink-0 items-center gap-2 leading-none md:mr-6"
            >
              <span
                aria-hidden="true"
                data-testid="navbar-brand-mark"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 17, fontWeight: 400, lineHeight: 1 }}
                className="flex aspect-square h-7 w-7 shrink-0 items-center justify-center border border-ink/80 text-ink/80 transition-colors group-hover:border-ink group-hover:text-ink"
              >
                E
              </span>
              {runtimeEnvironment.isNonProduction && (
                <span
                  data-testid="runtime-environment-label"
                  className="rounded border border-[#c4953a]/50 bg-[#c4953a]/10 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-normal text-[#8a641e] dark:text-[#d8b15e]"
                >
                  {runtimeEnvironment.isLiveStaging
                    ? <><span className="block text-center leading-tight">Live</span><span className="block text-center leading-tight">Staging</span></>
                    : runtimeEnvironment.headerLabel}
                </span>
              )}
            </Link>

            {/* Desktop tab links */}
            <nav className="hidden md:flex items-center h-12 flex-1 gap-0">
              {TOP_NAV.map(({ to, label, match, alsoMatch }) => {
                const isActive = location.pathname.startsWith(match)
                  || (alsoMatch && location.pathname.startsWith(alsoMatch))
                const submenuItems = SUB_NAV[match] || []

                if (submenuItems.length === 0) {
                  return (
                    <Link key={match} to={to} className={topTabCls(isActive)}>
                      {label}
                    </Link>
                  )
                }

                const isOpen = desktopMenuOpen === match
                const menuId = `desktop-nav-${label.toLowerCase().replaceAll(' ', '-')}`

                return (
                  <div
                    key={match}
                    data-desktop-nav-section={match}
                    className="relative flex h-12 items-center"
                    onMouseEnter={() => setDesktopMenuOpen(match)}
                    onMouseLeave={(event) => {
                      if (!event.currentTarget.contains(document.activeElement)) {
                        setDesktopMenuOpen(null)
                      }
                    }}
                    onFocus={() => setDesktopMenuOpen(match)}
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget)) {
                        setDesktopMenuOpen(null)
                      }
                    }}
                  >
                    <Link
                      to={to}
                      aria-haspopup="true"
                      aria-expanded={isOpen}
                      aria-controls={menuId}
                      className={topTabCls(isActive)}
                      onClick={() => setDesktopMenuOpen(null)}
                    >
                      {label}
                    </Link>

                    <nav
                      id={menuId}
                      aria-label={`${label} navigation`}
                      aria-hidden={!isOpen}
                      className={`absolute left-0 top-full z-50 w-56 overflow-hidden rounded-b-xl border border-border bg-canvas py-1.5 shadow-xl transition-[opacity,transform,visibility] duration-150 ease-out motion-reduce:transition-none ${
                        isOpen
                          ? 'visible translate-y-0 opacity-100'
                          : 'invisible pointer-events-none -translate-y-1 opacity-0'
                      }`}
                    >
                      {submenuItems.map((item) => {
                        const submenuTo = resolvedNavTarget(item)
                        return (
                          <Link
                            key={`${item.label}:${item.to}`}
                            to={submenuTo}
                            tabIndex={isOpen ? undefined : -1}
                            onClick={() => setDesktopMenuOpen(null)}
                            className={desktopMenuLinkCls(item.isActive
                              ? item.isActive(location.pathname)
                              : location.pathname === item.to)}
                          >
                            {item.label}
                          </Link>
                        )
                      })}
                    </nav>
                  </div>
                )
              })}
            </nav>

            {/* Right-side controls */}
            <div className="ml-auto flex items-center gap-1.5 md:gap-3 shrink-0 min-w-0">

              <DesktopNavbarStatus />
              <div data-testid="desktop-navbar-appearance-toggles" className="hidden md:flex items-center gap-3">
                <button
                  data-command-id="app.privacy.toggle" onClick={togglePrivacy}
                  data-command-label={privacyMode ? 'Show values' : 'Hide values'}
                  aria-label={privacyMode ? 'Show values' : 'Hide values'}
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-canvas2 border border-border text-inkMuted hover:text-ink hover:border-ink/30 transition-colors"
                >
                  {privacyMode ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <InterfaceColorControl userId={user?.id} dark={dark} onToggle={toggleDark} />
              </div>
              <NotificationButton />

              {/* User menu — desktop */}
              <div className="hidden md:block relative" ref={userMenuRef}>
                <button
                  aria-label="Account menu" data-command-id="app.account.open" onClick={() => setUserMenuOpen(o => !o)}
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-canvas2 border border-border text-inkMuted hover:text-ink hover:border-ink/30 transition-colors"
                  data-command-label="Account menu"
                >
                  <User size={14} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-10 w-48 bg-canvas border border-border rounded-xl shadow-lg py-1.5 z-50">
                    <div className="px-3 py-2 font-mono text-[10px] text-inkMuted truncate border-b border-border">
                      {user?.email}
                    </div>
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-semibold text-inkMuted hover:text-ink hover:bg-canvas2 transition-colors"
                    >
                      <Settings size={14} />
                      Settings
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-semibold text-inkMuted hover:text-ink hover:bg-canvas2 transition-colors w-full"
                    >
                      <LogOut size={14} />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>

              {/* Hamburger — mobile */}
              <button
                onClick={() => setMobileOpen(o => !o)}
                className="md:hidden p-1.5 text-ink"
                aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <PaperModeBanner />
      <MarketDataOutageAlert />

      {/* ── Mobile slide-out nav ───────────────────────────────────────── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          {/* Side panel */}
          <div className="relative w-[280px] max-w-[80vw] bg-canvas border-r border-border shadow-xl flex flex-col animate-slide-in-nav overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
              <Link
                to="/dashboard"
                onClick={() => setMobileOpen(false)}
                aria-label="Erevis home"
                className="group flex items-center gap-2"
              >
                <span
                  aria-hidden="true"
                  data-testid="navbar-brand-mark-mobile"
                  style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 17, fontWeight: 400, lineHeight: 1 }}
                  className="flex aspect-square h-7 w-7 shrink-0 items-center justify-center border border-ink/80 text-ink/80 transition-colors group-hover:border-ink group-hover:text-ink"
                >
                  E
                </span>
                {runtimeEnvironment.isNonProduction && (
                  <span
                    data-testid="runtime-environment-label-mobile"
                    className="inline-flex flex-col rounded border border-[#c4953a]/50 bg-[#c4953a]/10 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-normal text-[#8a641e] dark:text-[#d8b15e]"
                  >
                    {runtimeEnvironment.isLiveStaging
                    ? <><span className="block text-center leading-tight">Live</span><span className="block text-center leading-tight">Staging</span></>
                    : runtimeEnvironment.headerLabel}
                  </span>
                )}
              </Link>
              <button onClick={() => setMobileOpen(false)} className="p-1 text-inkMuted hover:text-ink">
                <X size={18} />
              </button>
            </div>

            {/* Nav items — ALL pages with ALL sub-pages always visible */}
            <nav className="flex-1 px-3 py-3 space-y-1">
              {TOP_NAV.map(({ to, label, match, alsoMatch }) => {
                const isActive = location.pathname.startsWith(match) || (alsoMatch && location.pathname.startsWith(alsoMatch))
                const subs = SUB_NAV[match] || (match === '/integrations' ? SUB_NAV['/integrations'] : null)
                return (
                  <div key={match}>
                    <Link
                      to={to}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                        isActive ? 'bg-canvas2 text-ink' : 'text-inkMuted hover:text-ink hover:bg-canvas2/50'
                      }`}
                    >
                      {label}
                    </Link>
                    {/* Always show sub-pages for sections that have them */}
                    {subs && subs.length > 0 && (
                      <div className="ml-3 mt-0.5 mb-1 pl-3 border-l border-border space-y-0.5">
                        {subs.map((subItem) => {
                          const subTo = resolvedNavTarget(subItem)
                          return (
                            <Link
                              key={`${subItem.label}:${subItem.to}`}
                              to={subTo}
                              onClick={() => setMobileOpen(false)}
                              className={`flex items-center px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                location.pathname === subItem.to
                                  ? 'text-ink bg-canvas2/50'
                                  : 'text-inkMuted hover:text-ink'
                              }`}
                            >
                              {subItem.label}
                            </Link>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </nav>

            {/* Footer — settings, sign out, status */}
            <div className="border-t border-border px-3 py-3 shrink-0 space-y-1">
              <Link
                to="/settings"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold text-inkMuted hover:text-ink hover:bg-canvas2/50 transition-colors"
              >
                <Settings size={16} />
                Settings
              </Link>
              <button
                onClick={() => { setMobileOpen(false); handleSignOut() }}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold text-inkMuted hover:text-ink hover:bg-canvas2/50 transition-colors w-full"
              >
                <LogOut size={16} />
                Sign Out
              </button>
              <div className="px-3 pt-1 font-mono text-[10px] text-inkDim truncate">
                {user?.email}
              </div>
              <MobileMenuStatus onNavigate={() => setMobileOpen(false)} />
            </div>
          </div>
        </div>
      )}

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-y-auto">
        <div
          data-testid="layout-content"
          className={
            isDashboard
              ? 'p-1 max-w-none flex-1 flex flex-col min-h-0 w-full'
              : isChartDemo
              ? 'px-0 py-0 max-w-none flex-1 flex flex-col min-h-0 w-full'
              : isPositionWorkspace
                ? 'px-4 md:px-8 py-0 max-w-screen-xl mx-auto flex-1 flex flex-col min-h-0 w-full'
              : 'px-4 md:px-8 py-6 max-w-screen-xl mx-auto flex-1 flex flex-col min-h-0 w-full'
          }
        >
          <MarketWidgetSession><RetainedDashboardOutlet key={user?.id || user?.email} /></MarketWidgetSession>
        </div>
      </main>

      {/* ── Notification viewport ─────────────────────────────────────── */}
      <NotificationViewport />

      </div>
      </AccountNicknamesProvider>
    </CommandProvider>
  )
}
